import { useState, useEffect, useMemo, useRef } from 'react';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { Download, MapPin, Fuel, Route, Map, AlertCircle, History, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import IFTABackfillModal from './IFTABackfillModal';

const HOME_STATE = 'NV';

// Generate available quarters from Q1-2026 through current
function getAvailableQuarters() {
  const quarters = [];
  const now = new Date();
  const currentQ = Math.ceil((now.getMonth() + 1) / 3);
  const currentYear = now.getFullYear();
  // Show last 2 years + current
  for (let year = currentYear - 1; year <= currentYear; year++) {
    const maxQ = year === currentYear ? currentQ : 4;
    for (let q = 1; q <= maxQ; q++) {
      quarters.push(`Q${q}-${year}`);
    }
  }
  return quarters.reverse();
}

function getCurrentQuarter() {
  const now = new Date();
  return `Q${Math.ceil((now.getMonth() + 1) / 3)}-${now.getFullYear()}`;
}

// Get date range [YYYY-MM-DD, YYYY-MM-DD] for a quarter string like "Q2-2026"
function getQuarterDateRange(quarterStr) {
  const [qPart, yearStr] = quarterStr.split('-');
  const q = parseInt(qPart.replace('Q', ''));
  const year = parseInt(yearStr);
  const startMonth = (q - 1) * 3; // 0-indexed
  const start = new Date(year, startMonth, 1);
  const end = new Date(year, startMonth + 3, 0); // last day of quarter
  return [start.toISOString().split('T')[0], end.toISOString().split('T')[0]];
}

function StatCard({ icon: Icon, label, value, color = 'text-primary', bg = 'bg-primary/10' }) {
  return (
    <div className="glass-card p-4">
      <div className={cn('w-9 h-9 rounded-xl flex items-center justify-center mb-3', bg)}>
        <Icon className={cn('w-4 h-4', color)} />
      </div>
      <p className="text-xl font-space font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

export default function IFTATab() {
  const { user } = useAuth();
  const [selectedQuarter, setSelectedQuarter] = useState(getCurrentQuarter());
  const [mileageLogs, setMileageLogs] = useState([]);
  const [fuelExpenses, setFuelExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backfillModal, setBackfillModal] = useState(null); // null | 'all' | 'retry'
  const [allLogs, setAllLogs] = useState([]);

  const quarters = useMemo(() => getAvailableQuarters(), []);

  // Guards against switching quarters faster than a fetch resolves -- a slow
  // response for a previously-selected quarter would otherwise land after a
  // newer one and overwrite it, mislabeling one quarter's mileage/fuel data
  // as another's. Also called directly from the backfill modal's onComplete,
  // where "always show whatever this call fetched" is exactly right.
  const latestRequestRef = useRef(0);

  useEffect(() => {
    if (!user?.email) return;
    loadData();
  }, [user?.email, selectedQuarter]);

  const loadData = async () => {
    const requestId = ++latestRequestRef.current;
    setLoading(true);
    const [logs, fuel, all] = await Promise.all([
      client.entities.IFTAMileageLog.filter(
        { driver_email: user.email, quarter: selectedQuarter },
        '-trip_date', 200
      ),
      client.entities.Expense.filter(
        { driver_email: user.email, category: 'Fuel' },
        '-date', 500
      ),
      client.entities.IFTAMileageLog.filter(
        { driver_email: user.email },
        '-trip_date', 200
      ),
    ]);
    if (requestId !== latestRequestRef.current) return; // superseded by a newer call
    setMileageLogs(logs);
    setAllLogs(all);
    // Filter fuel by quarter date range client-side
    const [startDate, endDate] = getQuarterDateRange(selectedQuarter);
    setFuelExpenses(fuel.filter(e => e.date >= startDate && e.date <= endDate));
    setLoading(false);
  };

  // Aggregate miles by state — exclude Invalid-Data logs
  const statesMileage = useMemo(() => {
    const map = {};
    for (const log of mileageLogs.filter(l => l.source !== 'Invalid-Data')) {
      let lines = [];
      try { lines = JSON.parse(log.state_mileage || '[]'); } catch { lines = []; }
      for (const { state, miles } of lines) {
        if (!state || !miles) continue;
        map[state] = (map[state] || 0) + miles;
      }
    }
    return Object.entries(map)
      .map(([state, miles]) => ({ state, miles: parseFloat(miles.toFixed(1)) }))
      .sort((a, b) => b.miles - a.miles);
  }, [mileageLogs]);

  const totalMiles = useMemo(() => statesMileage.reduce((s, x) => s + x.miles, 0), [statesMileage]);

  // Aggregate fuel by state
  const fuelByState = useMemo(() => {
    const map = {};
    for (const exp of fuelExpenses) {
      const st = exp.fuel_state;
      if (!st) continue;
      if (!map[st]) map[st] = { gallons: 0, amount: 0 };
      map[st].gallons += exp.gallons || 0;
      map[st].amount += exp.amount || 0;
    }
    return Object.entries(map)
      .map(([state, d]) => ({ state, gallons: parseFloat(d.gallons.toFixed(3)), amount: d.amount }))
      .sort((a, b) => b.gallons - a.gallons);
  }, [fuelExpenses]);

  const totalGallons = useMemo(() => fuelExpenses.reduce((s, e) => s + (e.gallons || 0), 0), [fuelExpenses]);
  const uniqueStates = useMemo(() => statesMileage.length, [statesMileage]);
  const hasFuelStateData = fuelByState.length > 0;
  const showBackfill = allLogs.length === 0 || mileageLogs.length === 0;
  const hasManualEmpty = useMemo(() => allLogs.some(log => {
    try { return log.source === 'Manual' && JSON.parse(log.state_mileage || '[]').length === 0; } catch { return false; }
  }), [allLogs]);

  const handleExport = () => {
    const rows = [['State', 'Miles Driven', 'Fuel Gallons', 'Fuel Amount ($)']];

    // Merge all states from both tables
    const allStates = new Set([
      ...statesMileage.map(s => s.state),
      ...fuelByState.map(s => s.state),
    ]);

    for (const state of [...allStates].sort()) {
      const miles = statesMileage.find(s => s.state === state)?.miles || 0;
      const fuel = fuelByState.find(s => s.state === state);
      rows.push([state, miles.toFixed(1), (fuel?.gallons || 0).toFixed(3), (fuel?.amount || 0).toFixed(2)]);
    }

    // Totals row
    const totalFuelAmount = fuelByState.reduce((s, x) => s + x.amount, 0);
    rows.push(['TOTAL', totalMiles.toFixed(1), totalGallons.toFixed(3), totalFuelAmount.toFixed(2)]);

    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const driverName = (user?.full_name || user?.email || 'Driver').replace(/\s+/g, '_');
    a.href = url;
    a.download = `IFTA-${selectedQuarter}-${driverName}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h3 className="font-space font-semibold text-foreground">IFTA Mileage Report</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Per-state miles and fuel for quarterly filing</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {showBackfill && (
            <button
              onClick={() => setBackfillModal('all')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-500/50 text-amber-400 bg-amber-500/5 text-sm font-semibold hover:bg-amber-500/15 transition-colors"
            >
              <History className="w-4 h-4" /> Import Historical Loads
            </button>
          )}
          {hasManualEmpty && (
            <button
              onClick={() => setBackfillModal('retry')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-muted-foreground text-xs font-medium hover:text-foreground hover:border-border transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Re-run for failed loads
            </button>
          )}
          <select
            value={selectedQuarter}
            onChange={e => setSelectedQuarter(e.target.value)}
            className="px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
          >
            {quarters.map(q => (
              <option key={q} value={q} className="bg-background">{q}</option>
            ))}
          </select>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Route} label="Total Miles" value={totalMiles.toLocaleString('en-US', { maximumFractionDigits: 0 })} color="text-primary" bg="bg-primary/10" />
        <StatCard icon={Fuel} label="Fuel Gallons" value={totalGallons > 0 ? totalGallons.toFixed(1) : '—'} color="text-amber-400" bg="bg-amber-500/10" />
        <StatCard icon={MapPin} label="Loads Tracked" value={mileageLogs.filter(l => l.source !== 'Invalid-Data').length} color="text-blue-400" bg="bg-blue-500/10" />
        <StatCard icon={Map} label="States Traveled" value={uniqueStates} color="text-green-400" bg="bg-green-500/10" />
      </div>

      {/* Miles by State */}
      <div className="glass-card overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Route className="w-4 h-4 text-primary" />
          <h4 className="font-space font-semibold text-foreground text-sm">Miles by State — {selectedQuarter}</h4>
        </div>
        {statesMileage.length === 0 ? (
          <div className="p-10 text-center">
            <MapPin className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No mileage logs found for {selectedQuarter}.</p>
            <p className="text-xs text-muted-foreground mt-1">Mileage is captured automatically when you mark a load delivered.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {/* Header */}
            <div className="grid grid-cols-3 px-5 py-2 text-xs text-muted-foreground font-semibold uppercase tracking-wide">
              <span>State</span>
              <span className="text-right">Miles Driven</span>
              <span className="text-right">% of Total</span>
            </div>
            {statesMileage.map(({ state, miles }) => {
              const pct = totalMiles > 0 ? (miles / totalMiles) * 100 : 0;
              const isHome = state === HOME_STATE;
              return (
                <div
                  key={state}
                  className={cn(
                    'grid grid-cols-3 px-5 py-3 text-sm hover:bg-white/3 transition-colors',
                    isHome && 'border-l-2 border-amber-500/60'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      'text-xs font-bold px-2 py-0.5 rounded-full border',
                      isHome
                        ? 'text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30'
                        : 'text-muted-foreground bg-muted/40 border-border'
                    )}>
                      {state}
                    </span>
                    {isHome && <span className="text-xs text-amber-400/70">home</span>}
                  </div>
                  <span className="text-right font-semibold text-foreground">{miles.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</span>
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 h-1.5 rounded-full bg-white/10 overflow-hidden hidden sm:block">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                    <span className="text-muted-foreground">{pct.toFixed(1)}%</span>
                  </div>
                </div>
              );
            })}
            {/* Totals */}
            <div className="grid grid-cols-3 px-5 py-3 text-sm bg-white/3 font-bold">
              <span className="text-foreground">TOTAL</span>
              <span className="text-right text-primary">{totalMiles.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</span>
              <span className="text-right text-muted-foreground">100%</span>
            </div>
          </div>
        )}
      </div>

      {/* Fuel by State */}
      <div className="glass-card overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Fuel className="w-4 h-4 text-amber-400" />
          <h4 className="font-space font-semibold text-foreground text-sm">Fuel Purchased by State — {selectedQuarter}</h4>
        </div>
        {!hasFuelStateData ? (
          <div className="p-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-foreground font-medium">No state fuel data for {selectedQuarter}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Add state info to your fuel expenses to complete your IFTA report.
                {fuelExpenses.length > 0 && ` You have ${fuelExpenses.length} fuel expense${fuelExpenses.length !== 1 ? 's' : ''} this quarter without state data.`}
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-border">
            <div className="grid grid-cols-3 px-5 py-2 text-xs text-muted-foreground font-semibold uppercase tracking-wide">
              <span>State</span>
              <span className="text-right">Gallons</span>
              <span className="text-right">Amount Spent</span>
            </div>
            {fuelByState.map(({ state, gallons, amount }) => (
              <div key={state} className="grid grid-cols-3 px-5 py-3 text-sm hover:bg-white/3 transition-colors">
                <span className="text-xs font-bold px-2 py-0.5 rounded-full border text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30 w-fit">
                  {state}
                </span>
                <span className="text-right font-semibold text-foreground">{gallons.toFixed(3)}</span>
                <span className="text-right text-amber-400">${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            ))}
            <div className="grid grid-cols-3 px-5 py-3 text-sm bg-white/3 font-bold">
              <span className="text-foreground">TOTAL</span>
              <span className="text-right text-amber-400">{totalGallons.toFixed(3)} gal</span>
              <span className="text-right text-amber-400">${fuelByState.reduce((s, x) => s + x.amount, 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        )}
      </div>

      {/* Disclaimer */}
      <p className="text-xs text-muted-foreground leading-relaxed px-1">
        Miles are calculated using PC*Miler, the industry standard for IFTA reporting. This report is for reference — consult your accountant or IFTA administrator for official filing.
      </p>

      {backfillModal && (
        <IFTABackfillModal
          mode={backfillModal}
          onClose={() => setBackfillModal(null)}
          onComplete={loadData}
        />
      )}
    </div>
  );
}